import { useCallback } from "react";
// import { DeviceEventEmitter } from "~/ui/primitives"
import { useStores, type Chat } from "~models"
import { Event, EventNames, FLAG_ACK, FLAG_REPLY, ChatMessagePinData, ChatUpdateData, ChatDeleteData, ChatMemberARData, ChatTypes, FriendCommonData } from "~proto"
import useNavigation from "~hooks/useNavigation"
import { globalToast } from "~utils"

function isWsChannelCid(value: unknown): boolean {
  const s = String(value ?? "").trim()
  return s.length >= 12 && !/^\d+$/.test(s)
}

/** CHAT_CHANNEL_FUNDS_CHANGED(126) 需要 WS 长 chat_id 字符串（与 RN 端 current.chat_id 一致） */
function resolveChannelFundsChatId(
  chatStore: { chats: { values: () => Iterable<Chat> } },
  chat: Chat | null | undefined,
  data: { chat_id?: number | string; chat_cid?: string },
): string | null {
  for (const ref of [data.chat_cid, data.chat_id, chat?.chat_id]) {
    if (isWsChannelCid(ref)) return String(ref).trim()
  }

  const numericId =
    (typeof data.chat_id === "number" && data.chat_id > 0 ? data.chat_id : null) ??
    (/^\d+$/.test(String(data.chat_id ?? "")) ? Number(data.chat_id) : null) ??
    (chat?.id && chat.id > 0 ? chat.id : null)

  if (numericId != null) {
    for (const c of chatStore.chats.values()) {
      if (c.id === numericId && isWsChannelCid(c.chat_id)) {
        return String(c.chat_id).trim()
      }
    }
  }

  for (const m of chat?.messages ?? []) {
    if (isWsChannelCid(m.chat_cid)) return String(m.chat_cid).trim()
  }

  const legacy = chat?.chat_id ?? data.chat_cid
  if (legacy != null && !/^\d+$/.test(String(legacy))) {
    return String(legacy).trim()
  }

  return null
}

/** CHAT_SETTING_INFO(127) 与上压一致：优先 WS 长 chat_id，勿同时传数字 id + chat_cid */
function resolveChannelSettingChatId(
  chatStore: { chats: { values: () => Iterable<Chat> } },
  chat: Chat | null | undefined,
  refs?: { chat_id?: number | string; chat_cid?: string },
): number | string | null {
  const wsCid = resolveChannelFundsChatId(chatStore, chat, refs ?? {})
  if (wsCid) return wsCid

  const numeric =
    (typeof refs?.chat_id === "number" && refs.chat_id > 0 ? refs.chat_id : null) ??
    (/^\d+$/.test(String(refs?.chat_id ?? "")) ? Number(refs?.chat_id) : null) ??
    (chat?.id != null && chat.id > 0 ? chat.id : null)

  if (numeric != null) return numeric
  const legacy = refs?.chat_cid ?? chat?.chat_id ?? refs?.chat_id
  return legacy != null && String(legacy).trim() !== "" ? legacy : null
}

function buildChannelSettingInfoPayload(
  chatStore: { chats: { values: () => Iterable<Chat> } },
  chat: Chat | null | undefined,
  refs?: { chat_id?: number | string; chat_cid?: string },
) {
  const chat_id = resolveChannelSettingChatId(chatStore, chat, refs)
  if (chat_id == null) return null
  return { chat_id, type: ChatTypes.CHANNEL }
}

/** 扫码/搜索加入群或频道前，若本地尚无会话则先占位，否则 CHAT_MEMBER_ADDED 无法合并成员、会话列表无入口 */
export type EnsureLocalChatWhenJoiningGroupParams = {
  id: number
  /** 客户端 chat_id（与请求里的 chat_cid 一致） */
  chat_id: string
  title?: string
  image?: string | null
  isChannel: boolean
}
const useGroupActions = () => {
  const navigation = useNavigation()
  const { eventStore, chatStore, userStore, friendStore } = useStores()
  const current = chatStore.current

  const ensureLocalChatWhenJoiningGroup = useCallback(
    (p: EnsureLocalChatWhenJoiningGroupParams) => {
      const cid = String(p.chat_id).trim()
      if (!cid) return
      const cu = userStore.currentUser
      const members = [{ user_id: cu.id, user: cu }] as any
      const title =
        p.title && String(p.title).trim() !== ""
          ? p.title
          : p.isChannel
            ? "频道"
            : "群聊"
      const base = {
        id: p.id,
        chat_id: cid,
        name: cid,
        title,
        image: p.image ?? "",
        members,
        type: p.isChannel ? ChatTypes.CHANNEL : ChatTypes.GROUP,
      }
      if (p.isChannel) {
        chatStore.newChannelChat(base)
      } else {
        chatStore.newGroupChat(base)
      }
      const chat =
        chatStore.getChatByMessageRef?.({ chat_id: p.id, chat_cid: cid }) ??
        chatStore.byChatId?.(cid) ??
        chatStore.chats?.get?.(cid)
      chat?.setDeleted(false)
    },
    [chatStore, userStore],
  )

  /** 修改群名称 */
  const updateGroupTitle = useCallback((data) => {
    const evt = new Event<ChatUpdateData>(EventNames.CHAT_UPDATE, data)
    eventStore.publish(evt, { flags: FLAG_ACK })
    globalToast.show(`${data.type === ChatTypes.CHANNEL ? "频道" : "群"}信息已更新`)
  }, [eventStore])

  /** 修改群头像 */
  const updateGroupAvatar = useCallback((data) => {
    const evt = new Event(EventNames.CHAT_UPDATE, data)
    eventStore.publish(evt, { flags: FLAG_ACK })
    globalToast.show(`${data.type === ChatTypes.CHANNEL ? "频道" : "群"}头像已更新`)
    navigation.goBack()
  }, [eventStore])

  /** 任命管理员 */
  const appointAdmin = useCallback((user_id: number) => {
    const evt = new Event(EventNames.CHAT_UPDATE, {
      chat_id: current?.chat_id,
      user_id,
    })

    eventStore.publish(evt, { flags: FLAG_ACK })
  }, [eventStore])

  /** 设置群成员角色 */
  const setGroupMemberRole = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_MEMBER_PERM, {
      chat_id: current?.id,
      ...data,
    })
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  /** 频道设置录入游戏群 */
  const joinGameGroup = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_LINK_ADD_GROUP, {
      channel_id: data.channel_id,
      group_id: data.group_id,
    })
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  // 关联游戏群
  const relatedGameGroup = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_LINK_GROUP, {
      channel_id: data.channel_id,
      group_id: data.group_id,
      is_active: data.is_active,
    })
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  /** 获取频道配置信息请求（ACK，服务端可能推送 128） */
  const getChannelChatInfo = useCallback((chat_id: number | string, chat_cid?: string) => {
    const numeric =
      typeof chat_id === "number"
        ? chat_id
        : /^\d+$/.test(String(chat_id))
          ? Number(chat_id)
          : null
    const chat =
      chatStore.getChatByMessageRef?.({
        chat_id: numeric,
        chat_cid: chat_cid ?? (typeof chat_id === "string" ? chat_id : undefined),
      }) ?? chatStore.current

    const payload = buildChannelSettingInfoPayload(chatStore, chat, {
      chat_id,
      chat_cid,
    })
    if (!payload) return

    const evt = new Event(EventNames.CHAT_SETTING_INFO, payload as any)
    eventStore.publish(evt, { flags: FLAG_ACK })
  }, [eventStore, chatStore])

  /** 获取频道配置（含 link_groups），WS REPLY */
  const getChannelChatInfoReply = useCallback(
    (chatOrId: Chat | number, chat_cid?: string) => {
      let chat: Chat | null | undefined = chatStore.current
      let refs: { chat_id?: number | string; chat_cid?: string } = {}

      if (typeof chatOrId === "object" && chatOrId != null) {
        chat = chatOrId
        refs = { chat_id: chat.id, chat_cid: chat.chat_id }
      } else {
        const numeric =
          typeof chatOrId === "number"
            ? chatOrId
            : /^\d+$/.test(String(chatOrId))
              ? Number(chatOrId)
              : null
        refs = { chat_id: chatOrId, chat_cid }
        chat =
          chatStore.getChatByMessageRef?.({ chat_id: numeric, chat_cid }) ?? chatStore.current
      }

      const payload = buildChannelSettingInfoPayload(chatStore, chat, refs)
      if (!payload) {
        return Promise.reject(new Error("频道信息异常"))
      }

      const evt = new Event(EventNames.CHAT_SETTING_INFO, payload as any)
      return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
    },
    [eventStore, chatStore],
  )

  /** 置顶 / 取消置顶消息（群主/管理员） */
  const pinGroupMessage = useCallback(async (data: ChatMessagePinData) => {
    const evt = new Event<ChatMessagePinData>(EventNames.CHAT_MESSAGE_PIN, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  /** 上压资金和下压资金 */
  const onUpwardAndDownwardFunds = useCallback((data, onData, onError) => {
    const chat = chatStore.current
    const wsChatId = resolveChannelFundsChatId(chatStore, chat, data)

    if (!wsChatId) {
      onError?.({ message: "频道信息异常，请返回后重试" })
      return
    }

    const evt = new Event(EventNames.CHAT_CHANNEL_FUNDS_CHANGED, {
      chat_id: wsChatId,
      type: data.type,
      amount: data.amount,
    })
    eventStore.publish(evt, {
      flags: FLAG_REPLY,
      onData: (responseData: unknown) => {
        onData?.(responseData)
      },
      onError,
    })
  }, [eventStore, chatStore])

  // 转账
  const onChatTransfer = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_TRANSFER, data)
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

   // 发红包
  const onChatRedPacket = useCallback((data) => {
    const evt = new Event(EventNames.CHAT_REDPACKET_SEND, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  // 解散/退出后：本地移除、提示并回到聊天列表（与 chatStore.publicDismissGroupChat 一致，不用 publishAsync+FLAG_ACK）
  const finishLeaveOrDissolve = useCallback(
    (target: Chat, successMessage: string) => {
      chatStore.remove(target)
      globalToast.show(successMessage)
      eventStore.publish(new Event(EventNames.CHAT_LIST, {}), { flags: FLAG_REPLY })
      navigation.replace("ChatList")
    },
    [chatStore, eventStore, navigation],
  )

  // 群聊解散
  const handleAdminDissolveGroup = useCallback(
    (chat?: Chat | null) => {
      const target = chat ?? current
      if (!target) return

      const evt = new Event<ChatDeleteData>(EventNames.CHAT_DELETE, {
        chat_id: target.id,
        chat_cid: target.chat_id,
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
      finishLeaveOrDissolve(target, "解散成功")
    },
    [current, eventStore, finishLeaveOrDissolve],
  )

  // 群聊成员退出
  const handleMemberLeftGroup = useCallback(
    (chat?: Chat | null) => {
      const target = chat ?? current
      if (!target) return

      const evt = new Event<ChatDeleteData>(EventNames.CHAT_MEMBER_LEAVE, {
        chat_id: target.id,
        chat_cid: target.chat_id,
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
      finishLeaveOrDissolve(target, "退出成功")
    },
    [current, eventStore, finishLeaveOrDissolve],
  )

  // 群聊成员退出/取消频道(Promise)
  const handleMemberKickedOutGroup = useCallback(async(data) => {
    const evt = new Event(EventNames.CHAT_MEMBER_LEAVE, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  // 移除群聊成员
  const handleRemoveGroupMember = useCallback((data) => {
    const evt = new Event<ChatMemberARData>(EventNames.CHAT_MEMBER_REMOVE, {
      chat_id: current?.id,
      chat_cid: current?.chat_id,
      user_ids: data.user_ids,
    })
    eventStore.publish(evt, { flags: FLAG_ACK })
    navigation.goBack()
  }, [eventStore])

  // 添加群聊成员
  const handleAddGroupMember = useCallback((data) => {
    const evt = new Event(EventNames.CHAT_MEMBER_ADD, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  // 转账领取
  const handleChatTransfer = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_TRANSFER_RECEIVE, data)
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  // 红包领取
  const handleRedPacket = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_REDPACKET_GRAB, data)
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  // 转账详情
  const handleTransferDetail = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_TRANSFER_DATA, data)
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  // 红包详情
  const handleRedPacketDetail = useCallback((data, onData, onError) => {
    const evt = new Event(EventNames.CHAT_REDPACKET_DATA, data)
    eventStore.publish(evt, { flags: FLAG_REPLY, onData, onError })
  }, [eventStore])

  // 创建聊天室
  const handleCreateChatRoom = useCallback(
    async (data) => {
      const { localMembers, members, ...createPayload } = data || {}

      // 先本地创建 chat，确保页面可立即 setCurrent(chat)
      // const chat = chatStore.newGroupChat({
      //   type: createPayload.type,
      //   creator_id: createPayload.creator_id,
      //   image: createPayload.image,
      //   title: createPayload.title,
      //   members: localMembers || [],
      // })

      const evt = new Event(EventNames.CHAT_NEW, {
        ...createPayload,
        chat_id: data.chat_id,
        title: data.title,
        image: data.image,
        members,
      })
      const res = await eventStore.publishAsync(evt, { flags: FLAG_REPLY })
      return { data, res }
    },
    [eventStore, chatStore],
  )

  // 群公告
  const handleChatAnnouncement = useCallback((data) => {
    const evt = new Event(EventNames.CHAT_ANNOUNCEMENT, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  // 添加好友
  const handleAddFriend = useCallback((data) => {
    const evt = new Event(EventNames.FRIEND_ADD, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY }).then((reply) => {
      friendStore.onFriendAddReply(reply, data)
      friendStore.markSkipRefreshUntil()
      window.setTimeout(() => friendStore.refreshFriendLists(), 1600)
      return reply
    })
  }, [eventStore, friendStore])

  // 同意好友
  const handleApproveFriend = useCallback((data) => {
    const evt = new Event<FriendCommonData>(EventNames.FRIEND_APPROVE, data)
    eventStore.publish(evt, { flags: FLAG_ACK })
  }, [eventStore])

  // 拒绝好友
  const handleRejectFriend = useCallback((data) => {
    const evt = new Event<FriendCommonData>(EventNames.FRIEND_REJECT, data)
    eventStore.publish(evt, { flags: FLAG_ACK })
  }, [eventStore])

  // 获取用户/频道/群组信息
  const handleGetInfo = useCallback((data) => {
    const evt = new Event(EventNames.GET_INFO, data)
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  // 全部已读
  const handleChatMessageReadAll = useCallback((data) => {
    const evt = new Event(EventNames.CHAT_MESSAGE_READ_ALL, data)
    console.log("全部已读",evt,data,'data');
    return eventStore.publishAsync(evt, { flags: FLAG_REPLY })
  }, [eventStore])

  return {
    updateGroupTitle,
    updateGroupAvatar,
    appointAdmin,
    setGroupMemberRole,
    joinGameGroup,
    relatedGameGroup,
    getChannelChatInfo,
    getChannelChatInfoReply,
    pinGroupMessage,
    onUpwardAndDownwardFunds,
    onChatTransfer,
    onChatRedPacket,
    handleAdminDissolveGroup,
    handleMemberLeftGroup,
    handleMemberKickedOutGroup,
    handleRemoveGroupMember,
    handleAddGroupMember,
    handleChatTransfer,
    handleRedPacket,
    handleTransferDetail,
    handleRedPacketDetail,
    handleCreateChatRoom,
    handleChatAnnouncement,
    handleAddFriend,
    handleApproveFriend,
    handleRejectFriend,
    handleGetInfo,
    handleChatMessageReadAll,
    ensureLocalChatWhenJoiningGroup,
  }
}

export default useGroupActions
